/**
 * Type definitions for rich input with @ mentions and / commands
 * Used by RichMessageInput, MentionAutocomplete, CommandPalette
 */

/**
 * Entity types that can be mentioned with @
 */
export type MentionEntityType =
  | 'policy'
  | 'attribute'
  | 'subject'
  | 'classification'

/**
 * A mentionable entity resolved from search
 */
export interface MentionEntity {
  readonly id: string
  readonly entityType: MentionEntityType
  readonly displayName: string
  readonly description?: string
  readonly meta?: Readonly<Record<string, unknown>>
}

/**
 * Plain text segment in the input
 */
export interface TextSegment {
  readonly type: 'text'
  readonly text: string
}

/**
 * Mention segment representing an @ entity reference
 */
export interface MentionSegment {
  readonly type: 'mention'
  readonly entity: MentionEntity
}

/**
 * A single piece of the input -- either text or mention
 */
export type InputSegment = TextSegment | MentionSegment

/**
 * Slash command category for grouping in the palette
 */
export type SlashCommandCategory =
  | 'policy'
  | 'attributes'
  | 'compliance'
  | 'data'
  | 'subjects'

/**
 * Definition of a slash command
 */
export interface SlashCommand {
  readonly id: string
  readonly name: string
  readonly label: string
  readonly description: string
  readonly icon: string
  readonly category: SlashCommandCategory
  readonly requiresArgs?: boolean
  readonly argPlaceholder?: string
}

/**
 * Serialized payload sent to the API
 * Maps to existing SendMessageRequest fields (content, contextData, systemPrompt)
 */
export interface RichMessagePayload {
  readonly content: string
  readonly mentions: readonly MentionEntity[]
  readonly command?: string
}

/**
 * Mode the autocomplete dropdown is operating in
 */
export type AutocompleteMode = 'mention' | 'command' | 'none'

/**
 * Tracks the state of the autocomplete popover
 */
export interface AutocompleteState {
  readonly mode: AutocompleteMode
  readonly query: string
  readonly cursorIndex: number
  readonly selectedIndex: number
  readonly triggerStartIndex: number
}

/**
 * Initial (closed) autocomplete state
 */
export const AUTOCOMPLETE_INITIAL: AutocompleteState = {
  mode: 'none',
  query: '',
  cursorIndex: 0,
  selectedIndex: 0,
  triggerStartIndex: 0,
}

/**
 * Entity type display labels
 */
export const ENTITY_TYPE_LABELS: Record<MentionEntityType, string> = {
  policy: 'Policy',
  attribute: 'Attribute',
  subject: 'Subject',
  classification: 'Classification',
}

/**
 * Entity type icon names (Lucide icon identifiers)
 */
export const ENTITY_TYPE_ICONS: Record<MentionEntityType, string> = {
  policy: 'FileText',
  attribute: 'Tag',
  subject: 'User',
  classification: 'Shield',
}
